import {
  API_ENDPOINTS,
  ApiResponse,
  MasterQuestion,
  CreateMasterQuestionDto,
  UpdateMasterQuestionDto
} from '../api';
import httpClient from '../httpClient';

export const masterQuestionService = {
  getList: async (params?: {
    search?: string;
    page?: number;
    limit?: number;
    type?: string;
    group?: string;
    groups?: string[];
  }) => {
    try {
      const queryParams = {
        pageNumber: params?.page || 1,
        pageSize: params?.limit || 10,
        search: params?.search || '',
        group: params?.group || ''
      };

      const payload: {
        group?: string[];
        type?: string;
      } = {};

      if (params?.groups && params.groups.length > 0) {
        payload.group = params.groups;
      } else if (params?.group && params.group !== '') {
        payload.group = params.group.includes(',')
          ? params.group
              .split(',')
              .map((g) => g.trim())
              .filter(Boolean)
          : [params.group];
      }

      if (params?.type && params.type !== '') {
        payload.type = params.type;
      }

      const response = await httpClient.post<ApiResponse<MasterQuestion[]>>(
        API_ENDPOINTS.MASTER_QUESTION.LIST,
        payload,
        {
          params: queryParams
        }
      );
      return response.data;
    } catch {
      return {
        pageNumber: params?.page || 1,
        pageSize: params?.limit || 10,
        totalPages: 0,
        totalRecords: 0,
        message: 'Failed to fetch data',
        data: []
      };
    }
  },

  getExternalQuestionTypeList: async (params?: { search?: string }) => {
    const response = await httpClient.get<ApiResponse<any>>(
      API_ENDPOINTS.MASTER_QUESTION.EXTERNAL_QUESTION_TYPE_LIST,
      {
        params: {
          pageNumber: 1,
          pageSize: 100,
          search: params?.search || ''
        }
      }
    );
    return response.data;
  },

  getDetail: async (id: string) => {
    const response = await httpClient.get(API_ENDPOINTS.MASTER_QUESTION.DETAIL(id));

    return response.data;
  },

  create: async (data: CreateMasterQuestionDto) => {
    const response = await httpClient.post(API_ENDPOINTS.MASTER_QUESTION.CREATE, data);
    return response.data;
  },

  update: async (id: string, data: UpdateMasterQuestionDto) => {
    const response = await httpClient.put(API_ENDPOINTS.MASTER_QUESTION.UPDATE, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await httpClient.delete(API_ENDPOINTS.MASTER_QUESTION.DELETE, {
      data: { msQuestionID: id }
    });
    return response.data;
  },

  copy: async (id: string) => {
    const response = await httpClient.post(API_ENDPOINTS.MASTER_QUESTION.COPY, {
      id
    });
    return response.data;
  },

  bulkImport: async (
    groupId: string,
    questions: Array<{
      title: string;
      description?: string;
      type: 'Choose One' | 'Range' | 'Free-text';
      category?: string;
      skalaCategory?: string;
      rangeItems?: Array<{
        label: string;
        value: string;
        sequence: number;
      }>;
    }>
  ) => {
    const importPromises = questions.map(async (question, index) => {
      const createDto = {
        Title: question.title,
        Category: question.category
          ? question.category
              .split(',')
              .map((cat) => cat.trim())
              .join(', ')
          : '', // Handle multiple categories properly
        Description: question.description || '',
        Type: question.type,
        MsGroupQuestionID: groupId,
        Sequence: index + 1,
        SkalaCategory: question.skalaCategory || '',
        RangeItem:
          question.type === 'Range' && question.rangeItems ? question.rangeItems : undefined
      };

      try {
        const response = await httpClient.post(API_ENDPOINTS.MASTER_QUESTION.CREATE, createDto);
        return { success: true, data: response.data, question: question.title };
      } catch (error: any) {
        return {
          success: false,
          error: error?.response?.data?.message || 'Failed to create question',
          question: question.title
        };
      }
    });

    const results = await Promise.all(importPromises);

    const successful = results.filter((r) => r.success);
    const failed = results.filter((r) => !r.success);

    return {
      successful: successful.length,
      failed: failed.length,
      errors: failed.map((f) => ({ question: f.question, error: f.error })),
      details: {
        successfulQuestions: successful.map((s) => s.question),
        failedQuestions: failed.map((f) => f.question)
      }
    };
  }
};
